import { useCallback, useMemo } from 'react';
import { parsePath, useLocation, useNavigate, useParams } from 'react-router';

type AbsolutePath = `/${string}`;

type RoutePathController = Readonly<{
  current: string;
  parts: readonly string[];
  change: (path: AbsolutePath) => void;
  parameter: (name: string) => Readonly<{
    current: string | null;
  }>;
}>;

/*
path.current;                 // "/user/42"
path.parts;                   // ["user", "42"]
path.parameter('id').current; // "42"

path.change('/admin');
// "/admin?tab=activity#details"
*/
export function useUrlPathSync(): RoutePathController {
  const location = useLocation();
  const navigate = useNavigate();
  const parameters = useParams();

  const parts = useMemo(
    () => Object.freeze(location.pathname.split('/').filter(Boolean)),
    [location.pathname]
  );

  const change = useCallback(
    (value: AbsolutePath) => {
      const parsed = parsePath(value);

      if (parsed.search || parsed.hash) {
        throw new TypeError('path.change() only accepts a pathname');
      }

      navigate(
        {
          pathname: parsed.pathname,
          search: location.search,
          hash: location.hash,
        },
        {
          state: location.state,
        }
      );
    },
    [location.hash, location.search, location.state, navigate]
  );

  const parameter = useCallback(
    (name: string) =>
      Object.freeze({
        current: parameters[name] ?? null,
      }),
    [parameters]
  );

  return useMemo(
    () =>
      Object.freeze({
        current: location.pathname,
        parts,
        change,
        parameter,
      }),
    [location.pathname, parts, change, parameter]
  );
}
